import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../lib/AuthContext";
import {
  MagnifyingGlass, CircleNotch, CaretUp, CaretDown,
  Eye, ArrowRight, ArrowLeft, Crown, DownloadSimple,
  X, Trash, Warning, CheckCircle, User as UserIcon,
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";

const tierBadge = {
  free: "bg-slate-100 text-slate-600",
  pro: "bg-teal/10 text-teal-700",
  team: "bg-violet-50 text-violet-700",
  enterprise: "bg-amber-50 text-amber-700",
};

export default function AdminUsers() {
  const { authFetch } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, tier, status, sort_by: sortBy, sort_dir: sortDir, page, per_page: 25 });
      const res = await authFetch(`${window.location.origin}/api/admin/users?${params}`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setTotal(data.total);
        setTotalPages(data.total_pages);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch, search, tier, status, sortBy, sortDir, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return null;
    return sortDir === "asc" ? <CaretUp className="w-3 h-3" /> : <CaretDown className="w-3 h-3" />;
  };

  const exportCsv = () => {
    const header = "Name,Email,Tier,Status,Signed Up,Last Active,Lookups Today,Lookups Total\n";
    const rows = users.map(u =>
      `"${u.name || ""}","${u.email}","${u.tier}","${u.suspended ? "Suspended" : "Active"}","${u.created_at || ""}","${u.last_seen || ""}",${u.lookup_count_today || 0},${u.lookup_count_total || 0}`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "users.csv"; a.click();
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Users</h1>
          <p className="text-sm text-slate-500">{total} total users</p>
        </div>
        <Button onClick={exportCsv} variant="outline" size="sm" className="gap-2 rounded-lg">
          <DownloadSimple className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search name or email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
            data-testid="admin-users-search"
          />
        </div>
        <select value={tier} onChange={e => { setTier(e.target.value); setPage(1); }} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white" data-testid="admin-users-tier-filter">
          <option value="all">All Tiers</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="team">Team</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white" data-testid="admin-users-status-filter">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto" /></div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-400">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {[
                    { key: "name", label: "Name" },
                    { key: "email", label: "Email" },
                    { key: "tier", label: "Tier" },
                    { key: "created_at", label: "Signed Up" },
                    { key: "last_seen", label: "Last Active" },
                    { key: "lookup_count_today", label: "Today" },
                    { key: "lookup_count_total", label: "Total" },
                  ].map(({ key, label }) => (
                    <th key={key} onClick={() => toggleSort(key)} className="text-left px-4 py-3 font-semibold text-slate-600 cursor-pointer select-none hover:text-slate-900">
                      <span className="flex items-center gap-1">{label} <SortIcon col={key} /></span>
                    </th>
                  ))}
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.auth0_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-900">{u.name || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${tierBadge[u.tier] || tierBadge.free}`}>
                        {u.tier !== "free" && <Crown className="w-3 h-3" weight="fill" />}
                        {u.tier}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{u.last_seen ? new Date(u.last_seen).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono">{u.lookup_count_today || 0}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono">{u.lookup_count_total || 0}</td>
                    <td className="px-4 py-3">
                      {u.suspended
                        ? <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Suspended</span>
                        : <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Active</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelectedUser(u)} className="text-teal hover:text-teal-800 text-xs font-medium" data-testid={`admin-user-view-${u.email}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="gap-1 rounded-lg">
              <ArrowLeft className="w-4 h-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="gap-1 rounded-lg">
              Next <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* User Detail Panel */}
      {selectedUser && (
        <UserDetailPanel user={selectedUser} onClose={() => { setSelectedUser(null); fetchUsers(); }} authFetch={authFetch} />
      )}
    </div>
  );
}

function UserDetailPanel({ user: initialUser, onClose, authFetch }) {
  const [user, setUser] = useState(initialUser);
  const [notes, setNotes] = useState([]);
  const [actions, setActions] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newTier, setNewTier] = useState(user.tier);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${window.location.origin}/api/admin/users/${encodeURIComponent(user.auth0_id)}`);
        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
          setNotes(data.notes || []);
          setActions(data.actions || []);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, [authFetch, user.auth0_id]);

  const updateTier = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/admin/users/${encodeURIComponent(user.auth0_id)}/update-tier`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tier: newTier }),
      });
      if (res.ok) {
        setUser(prev => ({ ...prev, tier: newTier }));
        const detail = await authFetch(`${window.location.origin}/api/admin/users/${encodeURIComponent(user.auth0_id)}`);
        if (detail.ok) { const d = await detail.json(); setActions(d.actions || []); }
      }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const toggleSuspend = async () => {
    const endpoint = user.suspended ? "reactivate" : "suspend";
    try {
      const res = await authFetch(`${window.location.origin}/api/admin/users/${encodeURIComponent(user.auth0_id)}/${endpoint}`, { method: "POST" });
      if (res.ok) setUser(prev => ({ ...prev, suspended: !prev.suspended }));
    } catch (e) { console.error(e); }
  };

  const deleteUser = async () => {
    try {
      const res = await authFetch(`${window.location.origin}/api/admin/users/${encodeURIComponent(user.auth0_id)}`, { method: "DELETE" });
      if (res.ok) onClose();
    } catch (e) { console.error(e); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await authFetch(`${window.location.origin}/api/admin/users/${encodeURIComponent(user.auth0_id)}/notes`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note: newNote }),
      });
      if (res.ok) {
        setNewNote("");
        const detail = await authFetch(`${window.location.origin}/api/admin/users/${encodeURIComponent(user.auth0_id)}`);
        if (detail.ok) { const d = await detail.json(); setNotes(d.notes || []); }
      }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="admin-user-detail">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-display font-bold text-lg text-slate-900">User Detail</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Profile */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                <UserIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{user.name || "No name"}</p>
                <p className="text-sm text-slate-500">{user.email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-400">Auth0 ID:</span><p className="text-slate-700 font-mono text-xs break-all">{user.auth0_id}</p></div>
              <div><span className="text-slate-400">Signed up:</span><p className="text-slate-700">{user.created_at ? new Date(user.created_at).toLocaleString() : "—"}</p></div>
              <div><span className="text-slate-400">Last active:</span><p className="text-slate-700">{user.last_seen ? new Date(user.last_seen).toLocaleString() : "—"}</p></div>
              <div><span className="text-slate-400">Status:</span><p className={user.suspended ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>{user.suspended ? "Suspended" : "Active"}</p></div>
            </div>
          </div>

          {/* Plan Management */}
          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Plan Management</h3>
            <div className="flex items-center gap-2 mb-3">
              <select value={newTier} onChange={e => setNewTier(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white flex-1">
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="team">Team</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <Button onClick={updateTier} disabled={saving || newTier === user.tier} size="sm" className="bg-teal hover:bg-teal/90 text-white rounded-lg" data-testid="admin-update-tier-btn">
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : "Update"}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={toggleSuspend} variant="outline" size="sm" className={`rounded-lg ${user.suspended ? "text-emerald-600 border-emerald-200" : "text-amber-600 border-amber-200"}`}>
                {user.suspended ? "Reactivate" : "Suspend"}
              </Button>
              {!confirmDelete ? (
                <Button onClick={() => setConfirmDelete(true)} variant="outline" size="sm" className="rounded-lg text-red-600 border-red-200">
                  <Trash className="w-4 h-4 mr-1" /> Delete
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button onClick={deleteUser} size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-lg">Confirm Delete</Button>
                  <Button onClick={() => setConfirmDelete(false)} variant="outline" size="sm" className="rounded-lg">Cancel</Button>
                </div>
              )}
            </div>
          </div>

          {/* Usage */}
          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Usage</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-xl font-bold text-slate-900">{user.lookup_count_today || 0}</p><p className="text-xs text-slate-500">Today</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-xl font-bold text-slate-900">{user.lookup_count_total || 0}</p><p className="text-xs text-slate-500">All Time</p></div>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Admin Notes</h3>
            <div className="flex items-start gap-2 mb-3">
              <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." rows={2} className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500/30" />
              <Button onClick={addNote} size="sm" className="bg-slate-800 hover:bg-slate-900 text-white rounded-lg mt-0.5">Add</Button>
            </div>
            {notes.length > 0 && (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {notes.map((n, i) => (
                  <div key={i} className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <p className="text-sm text-slate-700">{n.note_text}</p>
                    <p className="text-xs text-slate-400 mt-1">{n.created_by_name} — {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Log */}
          <div className="border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Action Log</h3>
            {actions.length === 0 ? <p className="text-sm text-slate-400">No actions yet</p> : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {actions.map((a, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-3 text-sm">
                    <p className="font-medium text-slate-700">{a.action_type}: {a.old_value && `${a.old_value} → `}{a.new_value}</p>
                    <p className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}{a.notes ? ` — ${a.notes}` : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
